/**
 * EditOrgObjectiveDialog Component
 *
 * Dialog for editing an organization objective including team assignments
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Autocomplete,
    Checkbox,
    Chip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Edit, CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

export const EditOrgObjectiveDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { departments, setSuccess, setError, refetchData } = data;
    const { dialogs: dialogState, formState, updateFormField, closeEditOrgDialog } = dialogs;

    const [selectedDepartments, setSelectedDepartments] = useState<any[]>([]);

    const deptOptions = departments.map((d: any) => ({
        id: d.dept_id ?? d.id,
        name: d.dept_name ?? d.name,
    }));

    // Pre-populate selected departments from the objective's current team assignments
    useEffect(() => {
        if (dialogState.editOrgDialog && formState.selectedObjectiveForEdit) {
            const teamObjectives = formState.selectedObjectiveForEdit.teamObjectives || [];
            const currentDepts = teamObjectives
                .map((to: any) => {
                    const deptId = Number(to.department_id);
                    const match = deptOptions.find(d => Number(d.id) === deptId);
                    return match || null;
                })
                .filter(Boolean);
            setSelectedDepartments(currentDepts);
        }
    }, [dialogState.editOrgDialog, formState.selectedObjectiveForEdit]);

    const handleSave = async () => {
        if (!formState.selectedObjectiveForEdit?.id || !formState.newOrgTitle.trim()) return;

        try {
            const departmentIds = selectedDepartments.map(d => d.id);
            await okrApi.updateOrgObjective(
                formState.selectedObjectiveForEdit.id,
                formState.newOrgTitle,
                departmentIds
            );
            setSuccess('Organization objective updated successfully');
            handleClose();
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to update organization objective');
        }
    };

    const handleClose = () => {
        setSelectedDepartments([]);
        closeEditOrgDialog();
    };

    return (
        <Dialog
            open={dialogState.editOrgDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                    minHeight: '420px',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Edit sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Edit Organization Objective
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: '24px !important', pb: 2, overflowY: 'auto' }}>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        autoFocus
                        label="Objective Title"
                        fullWidth
                        value={formState.newOrgTitle}
                        onChange={(e) => updateFormField('newOrgTitle', e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <TextField
                        label="Description (Optional)"
                        fullWidth
                        multiline
                        rows={3}
                        value={formState.newOrgDescription}
                        onChange={(e) => updateFormField('newOrgDescription', e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <Autocomplete
                        multiple
                        options={deptOptions}
                        disableCloseOnSelect
                        getOptionLabel={(option) => option.name}
                        value={selectedDepartments}
                        onChange={(_, newValue) => setSelectedDepartments(newValue)}
                        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...rest } = props as any;
                            return (
                                <li key={key} {...rest}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option.name}
                                </li>
                            );
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...rest } = getTagProps({ index }) as any;
                                return (
                                    <Chip
                                        key={key}
                                        label={option.name}
                                        size="small"
                                        {...rest}
                                        sx={{
                                            bgcolor: 'rgba(245, 158, 11, 0.15)',
                                            color: '#f59e0b',
                                            fontWeight: 600,
                                        }}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Assigned Teams"
                                placeholder={selectedDepartments.length === 0 ? "Select teams..." : ""}
                                helperText="Teams with existing work cannot be unassigned"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!formState.newOrgTitle.trim()}
                    sx={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditOrgObjectiveDialog;
